import type {
  Entry,
  OptionalBaseProps,
  PageTypeResult,
  QuestionEntry,
  ValueType,
} from "senf-atomic-design-system/src/components/special/survey/results.config";
import {
  getRelativeLuminance,
  type HSL,
  hex2hsl,
  hsl2rgb,
  hsla,
  interpolate,
  type RGB,
} from "senf-atomic-design-system/src/styles/helpers";
import i18n from "senf-shared/utils/i18n";
import type { GroupKey, SurveyChartData, SurveyChartPoint } from "./types";

// Math constants
export const RADIAN = Math.PI / 180;

// Chart configuration constants
export const DEFAULT_CHART_TYPE = "bar";
export const DEFAULT_BASE_INDEX = 4; // Default base index for primary color palette

// Helper functions for pie chart label positioning
export const getCosSin = (midAngle: number): { sin: number; cos: number } => {
  const angleRadians = -RADIAN * midAngle;
  return { sin: Math.sin(angleRadians), cos: Math.cos(angleRadians) };
};

// Grouping configuration type
export type GroupingConfig = {
  key: string;
  label: string;
  getValue: (participant: Entry) => string;
  colors: Record<string, string>;
};

// Default grouping configurations
export const GROUPING_CONFIGS: Record<string, GroupingConfig> = {
  gender: {
    key: "gender",
    label: "Gender",
    getValue: (participant) => participant.userData.gender,
    colors: {
      male: "var(--tag-surface-selected-red)",
      female: "var(--tag-surface-selected-teal)",
      diverse: "var(--tag-surface-selected-yellow)",
    },
  },
  age: {
    key: "age",
    label: "Age Group",
    getValue: (participant) => {
      const age = participant.userData.age;
      if (age < 25) return "16-24";
      if (age <= 50) return "25-50";
      return "50+";
    },
    colors: {
      "16-24": "var(--tag-surface-selected-blue)",
      "25-50": "var(--tag-surface-selected-green)",
      "50+": "var(--tag-surface-selected-purple)",
    },
  },
};

// Helper function to process data by any grouping dimension
export const processDataByGroup = (
  participants: Entry[],
  dataProcessor: (participants: Entry[]) => SurveyChartData,
  groupConfig: GroupingConfig,
): {
  data: SurveyChartData;
  groupKeys: GroupKey[];
} => {
  // Get all unique group values
  const groupValues = Array.from(
    new Set(participants.map(groupConfig.getValue)),
  );

  // Get data for each group
  const groupResults: Record<string, SurveyChartData> = {};
  for (const groupValue of groupValues) {
    const groupParticipants = participants.filter(
      (p) => groupConfig.getValue(p) === groupValue,
    );
    groupResults[groupValue] = dataProcessor(groupParticipants);
  }

  // Combine all unique names (categories)
  const allNames = new Set<string>();
  for (const data of Object.values(groupResults)) {
    for (const point of data) {
      allNames.add(point.name);
    }
  }

  // Create grouped data points
  const data = Array.from(allNames).map((name) => {
    const basePoint: SurveyChartPoint = { name };

    // Add data for each group
    for (const groupValue of groupValues) {
      const groupPoint = groupResults[groupValue]?.find((p) => p.name === name);
      basePoint[groupValue] = groupPoint?.value || 0;
      basePoint[`${groupValue}Count`] = groupPoint?.count || 0;
    }

    // Calculate totals and percentages
    const totalCount = groupValues.reduce(
      (sum, gv) => sum + (Number(basePoint[`${gv}Count`]) || 0),
      0,
    );

    for (const groupValue of groupValues) {
      const count = Number(basePoint[`${groupValue}Count`]) || 0;
      basePoint[`${groupValue}Percent`] =
        totalCount > 0 ? count / totalCount : 0;
    }

    return basePoint;
  });

  // Create group keys for the chart
  const groupKeys = groupValues.map((value) => ({
    key: value,
    label: value.charAt(0).toUpperCase() + value.slice(1),
    color: groupConfig.colors[value] || "#6B7280", // Default gray if color not found
  }));

  return { data, groupKeys };
};

// Utility function to check if data is in SurveyChartData format
export const isSurveyChartData = (
  data: SurveyChartData | unknown[],
): data is SurveyChartData => {
  return (
    Array.isArray(data) &&
    data.length > 0 &&
    typeof data[0] === "object" &&
    data[0] !== null &&
    "name" in data[0]
  );
};

export const percentageFormatter = new Intl.NumberFormat(
  i18n.resolvedLanguage,
  {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  },
).format;

// Common chart validation
export const validateChartData = (data: unknown[]): boolean => {
  return Array.isArray(data) && data.length > 0;
};

// Utility to sort chart data with "Other" option last
export const sortChartData = (data: SurveyChartPoint[]): SurveyChartPoint[] => {
  return data.sort((a, b) => {
    if (
      Number.parseFloat(String(a.percent) || "0") < 0.2 &&
      a.name.toLowerCase() === "other"
    )
      return 1;
    if (
      Number.parseFloat(String(b.percent) || "0") < 0.2 &&
      b.name.toLowerCase() === "other"
    )
      return -1;
    return (b.value || 0) - (a.value || 0);
  });
};

// This is the newer version of the generateThemeColor function
const generateThemeColor = (
  color: RGB,
  options?: {
    n?: number;
    min?: number;
    max?: number;
    center?: number;
  },
) => {
  const { n = 10, min = 12, max, center } = options || {};
  const raw: Record<string, HSL> = {};
  const pretty: Record<string, string> = {};
  const [h, s, l] = color || [0, 0, 0];

  const clampedLum = center || Math.max(Math.min((l || 1) - 1, 89), 30);
  const clampedCenter = 50 * (1 / 3) + clampedLum * (2 / 3);
  const steps = Math.floor(n / 2);
  const lowerSteps = clampedCenter < 50 ? steps - 1 : steps - 1;
  const baseIndex = Math.max(Math.floor(clampedLum / 10) - 1, 3);

  const luminance = [
    ...interpolate({
      n: lowerSteps,
      from: min,
      to: clampedCenter,
    }),
    clampedCenter,
    ...interpolate({
      n: steps,
      from: max || 88 + Math.floor(clampedLum / 10),
      to: clampedCenter,
      reverse: true,
    }),
  ];

  // console.log(luminance);
  const clampedSaturation = (() => {
    if (s < 80) return 90;
    if (h > 160 && h < 170) return 50;
    if (h > 155 && h < 180) return 60;
    if (h > 150 && h < 190) return 70;
    if (h > 145 && h < 200) return 80;
    return 90;
  })();

  const saturation = [
    ...interpolate({ n: lowerSteps, from: Math.min(90, s), to: s }),
    s,
    ...interpolate({
      n: steps,
      from: Math.min(clampedSaturation, s),
      to: s,
      reverse: true,
    }),
  ];

  const hueRotation =
    h < 70 || (h > 270 && h < 360) ? 10 : h > 110 && h < 250 ? -10 : 0;

  const hue = [
    ...interpolate({ n: lowerSteps, from: h - hueRotation, to: h }),
    h,
    ...interpolate({
      n: steps,
      from: h + hueRotation,
      to: h,
      reverse: true,
    }),
  ];
  luminance.forEach((lum, i) => {
    const index = (n - i) * 100;
    raw[`${index}`] = [hue[i], saturation[i], lum];
    pretty[`${index}`] = hsla(...raw[`${index}`]);
  });

  const base = [
    baseIndex,
    baseIndex - 1,
    baseIndex - 2,
    Math.max(baseIndex - 4, 0),
  ].map((i) => ((n - i) * 100).toString()) as [
    `${number}`,
    `${number}`,
    `${number}`,
    `${number}`,
  ];

  return {
    raw,
    color: pretty,
    baseIndex: baseIndex,
    base,
  };
};

const palette: {
  [size: number]: {
    hsl: Record<string, string>;
    raw: Record<string, number[]>;
    invert: Record<string, boolean>;
  };
} = {};
const getShift = (size: number) => {
  return Math.min(2, size);
};
const generatePalette = (size: number, baseColor?: string) => {
  if (palette[size]) return palette[size];
  const { color, raw } = generateThemeColor(hex2hsl(baseColor || "#ffd061"), {
    n: Math.max(size + 3, 3),
    center: 60,
    min: 40,
  });

  const invert = Object.keys(raw).reduce(
    (acc, e) => {
      acc[e] = getRelativeLuminance(hsl2rgb(...raw[e])) < 140;
      return acc;
    },
    {} as Record<string, boolean>,
  );
  palette[size] = { hsl: color, raw, invert };
  return palette[size];
};

export const getColor = (index: number, array: any[], baseColor?: string) => {
  const { hsl, raw, invert } = generatePalette(array.length, baseColor);
  const colorIndex = index + getShift(array.length);

  const rawValues = Object.values(raw);
  const hslValues = Object.values(hsl);
  const invertValues = Object.values(invert);

  const strokeIndex =
    rawValues[colorIndex]?.[2] < 70
      ? colorIndex
      : rawValues.findIndex((e) => e?.[2] < 70);

  return {
    fill: hslValues[colorIndex],
    stroke: hslValues[strokeIndex],
    invert: invertValues[colorIndex],
  };
};
